/**
 * The sync engine: the piece that makes relaii feel instant and survive a bad
 * network.
 *
 * ── The algorithm, and why the order is not negotiable ──────────────────────
 *
 *   1. On mount, load the cursor and rooms from IndexedDB and render from cache
 *      immediately. Nothing waits for the network to paint.
 *   2. Call `GET /sync?since=<cursor>`, apply the events in order, advance the
 *      cursor, persist, and loop while `has_more`. If the server answers
 *      `reset: true` the cached cursor is no longer serviceable: wipe the cache
 *      and bootstrap from `since=0`.
 *   3. Only then open the WebSocket, joining `user:<id>` with `{since: cursor}`.
 *      If the join reply reports `missed > 0`, run step 2 again rather than
 *      trusting the socket to have back-filled anything.
 *   4. While online, apply socket events directly. The cursor follows them only
 *      as far as they are contiguous — see below.
 *   5. On disconnect, mark the connection offline. On reconnect, go back to
 *      step 2 *before* trusting live events again.
 *
 * That ordering is the whole correctness argument. The event log is the source
 * of truth and the socket is only a shortcut to it, so every path back to
 * "live" runs through `/sync` first. Live events that arrive while a catch-up
 * is in flight are buffered, not applied, because applying one would advance
 * the cursor past events `/sync` had not yet handed us — and the cursor
 * guarantee ("if you have seen N you have seen everything below N") is exactly
 * what would break.
 *
 * ── The socket sees less than `/sync` does ──────────────────────────────────
 *
 * `user:<id>` is fanned out to a room's members, while `/sync` answers for
 * every room the account may *read* — which includes this server's public
 * channels whether or not it has joined them. So a live client is not shown
 * everything its cursor would claim it had seen. Cursors are gap-free
 * server-wide, so a live event that skips one stands for an event the client
 * may still be owed: the position holds where it is and a catch-up `/sync`
 * fills the gap, rather than the cursor stepping over an event that would then
 * be below it for ever.
 *
 * State lives in a mutable store read through `useSyncExternalStore` rather
 * than in React state. Message volume makes re-render granularity matter: a
 * message arriving in one room must not re-render every other room's list.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from 'react'

import { needsMfaEnrollment, useAuth } from '@/api/auth'
import { unreadOf } from '@/api/types'
import { SESSION_EXPIRED_EVENT, api, isApiError } from '@/api/client'
import {
  loadCursor,
  loadIdentity,
  loadMessages,
  loadOutbox,
  loadRooms,
  removePending,
  removeRoom,
  refreshMessageAvatar,
  replaceRooms,
  resetCache,
  saveCursor,
  saveIdentity,
  saveMessages,
  saveRooms,
  savePending,
  type PendingMessage,
} from '@/api/cache'
import { RelaiiSocket, type ConnectionStatus, type EventOrigin } from '@/api/socket'
import { recordDiagnostic } from '@/lib/diagnostics'
import {
  assertNever,
  type Attachment,
  type CreateMessageRequest,
  type Cursor,
  type DurableEvent,
  type EphemeralEvent,
  type Message,
  type MessagePage,
  type PresenceStatus,
  type ReadStateRequest,
  type Room,
  type RoomUnread,
  type SyncResponse,
  type Timestamp,
  type User,
  type UUID,
} from '@/api/types'

// ── Tuning ──────────────────────────────────────────────────────────────────

const SYNC_PAGE_LIMIT = 500
const HISTORY_PAGE_LIMIT = 50

/** Live writes are coalesced so a busy room costs one transaction, not fifty. */
const PERSIST_DELAY_MS = 250

/** Live events held while catching up. Beyond this, resync instead of buffering. */
const LIVE_BUFFER_LIMIT = 5_000

/** Event ids remembered for de-duplication across the user and room topics. */
const APPLIED_EVENT_MEMORY = 4_000

/** How long a remote "typing" signal stands before it is assumed stale. */
const TYPING_TTL_MS = 7_000
const TYPING_SWEEP_MS = 1_000

/** Local send: repeat the signal at most this often, and stop after idling. */
const TYPING_REPEAT_MS = 3_000
const TYPING_IDLE_MS = 6_000

const RETRY_BASE_MS = 2_000
const RETRY_CAP_MS = 30_000

/**
 * How long a gap in the live cursor may stand before `/sync` is asked to fill
 * it.
 *
 * On a busy server the gap is permanent rather than occasional: every event in
 * every room this account is not a member of moves the global counter, so the
 * live stream almost never lands on `cursor + 1` and this timer becomes a poll.
 * That sets the interval, because the interval is the load — at 500 connected
 * clients, five seconds is 100 requests a second of nothing.
 *
 * What is being waited for bounds it from the other side. The live stream
 * carries every room this account belongs to; `/sync` adds only this server's
 * public channels it has *not* joined, where the sole thing worth learning is
 * that a channel appeared or was renamed. Half a minute is imperceptible for
 * that and six times cheaper.
 */
const CATCH_UP_DELAY_MS = 30_000

/**
 * Optimistic messages need a sort position above every real one. Real sequences
 * are per-room counters and will never approach this.
 */
const OPTIMISTIC_SEQUENCE_BASE = Number.MAX_SAFE_INTEGER - 1_000_000
const OPTIMISTIC_ID_PREFIX = 'pending:'

// ── Public shapes ───────────────────────────────────────────────────────────

export type { ConnectionStatus, PendingMessage }

export interface SyncSnapshot {
  rooms: Room[]
  cursor: Cursor
  /** True once there is something worth rendering: cached rooms, or a completed sync. */
  ready: boolean
}

export interface ConnectionSnapshot {
  status: ConnectionStatus
  lastError: string | null
}

export interface RoomMessagesSnapshot {
  /** Sorted by `room_sequence`, tombstones kept, optimistic sends last. */
  messages: Message[]
  /** Delivery state for the optimistic entries, matched on `client_message_id`. */
  pending: PendingMessage[]
  hasMore: boolean
  loading: boolean
  error: string | null
}

export interface RoomMessages extends RoomMessagesSnapshot {
  loadOlder: () => void
  retry: (clientMessageId: string) => void
  discard: (clientMessageId: string) => void
}

export interface PresenceEntry {
  status: PresenceStatus
  last_seen_at: Timestamp | null
}

/** A message shown before the server has acknowledged it. */
export function isOptimisticMessage(message: Message): boolean {
  return message.id.startsWith(OPTIMISTIC_ID_PREFIX)
}

// ── Small helpers ───────────────────────────────────────────────────────────

function describeError(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return 'The server could not be reached.'
}

/**
 * Whether the server has refused a request outright rather than failed it.
 *
 * A 4xx is a decision about the request itself — no membership, an archived
 * room, a body the server will not take — and posting the same request again
 * only asks for the same answer. The three exceptions ask for a retry in as
 * many words: 401 says the session ended, not that the message was wrong, and
 * 408 and 429 are "try this again".
 */
function isRefusal(error: unknown): boolean {
  if (!isApiError(error)) return false
  if (error.status === 401 || error.status === 408 || error.status === 429) return false
  return error.status >= 400 && error.status < 500
}

/**
 * A `client_message_id`.
 *
 * `crypto.randomUUID` is unavailable outside a secure context, and a relaii
 * server on a plain-HTTP LAN address is a perfectly ordinary deployment, so
 * there is a real fallback rather than a crash.
 */
function newClientMessageId(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()

    if (typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16))
      const byte = (index: number): number => bytes[index] ?? 0
      // RFC 4122 version and variant bits.
      bytes[6] = (byte(6) & 0x0f) | 0x40
      bytes[8] = (byte(8) & 0x3f) | 0x80
      const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }
  }

  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`
}

function retryDelay(attempts: number): number {
  const step = Math.min(Math.max(attempts, 1), 5)
  const ceiling = Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** (step - 1))
  return Math.round(ceiling / 2 + Math.random() * (ceiling / 2))
}

/**
 * Binary search by `room_sequence`. Sequences are unique and gap-free within a
 * room — deleted messages stay as tombstones precisely so that holds — which is
 * what lets the ordered list be maintained in place instead of resorted.
 */
function locate(
  ordered: readonly Message[],
  sequence: number,
): { index: number; found: boolean } {
  let low = 0
  let high = ordered.length

  while (low < high) {
    const middle = (low + high) >>> 1
    const entry = ordered[middle]
    if (entry === undefined) break
    if (entry.room_sequence < sequence) low = middle + 1
    else high = middle
  }

  const entry = ordered[low]
  return { index: low, found: entry !== undefined && entry.room_sequence === sequence }
}

// ── Internal state ──────────────────────────────────────────────────────────

interface RoomMessageCache {
  byId: Map<UUID, Message>
  byClientId: Map<string, UUID>
  ordered: Message[]
  historyBefore: number | null
  hasMore: boolean
  loading: boolean
  loadedHistory: boolean
  error: string | null
  snapshot: RoomMessagesSnapshot | null
}

function emptyRoomCache(): RoomMessageCache {
  return {
    byId: new Map<UUID, Message>(),
    byClientId: new Map<string, UUID>(),
    ordered: [],
    historyBefore: null,
    // Assumed true until a page from the server says otherwise, so scrolling up
    // in a room restored from cache still reaches for older history.
    hasMore: true,
    loading: false,
    loadedHistory: false,
    error: null,
    snapshot: null,
  }
}

interface ApplyOutcome {
  messages: Message[]
  rooms: Room[]
  removedRooms: UUID[]
  /** `client_message_id`s the server has now confirmed; their outbox rows can go. */
  confirmed: string[]
  /** Rooms the store has just met and holds no per-caller view of; fetch them. */
  joinedRooms: UUID[]
  /** True when a live event skipped a cursor, so `/sync` still owes us events. */
  gap: boolean
}

function emptyOutcome(): ApplyOutcome {
  return { messages: [], rooms: [], removedRooms: [], confirmed: [], joinedRooms: [], gap: false }
}

/** Replaces the public profile fields without leaking a current user's private fields into history. */
function userWithCurrentProfile(current: User, updated: User): User {
  return {
    ...current,
    username: updated.username,
    display_name: updated.display_name,
    qualified_id: updated.qualified_id,
    server_id: updated.server_id,
    is_local: updated.is_local,
    is_bot: updated.is_bot,
    is_admin: updated.is_admin,
    is_owner: updated.is_owner,
    status: updated.status,
    avatar_url: updated.avatar_url,
    bio: updated.bio,
    linkedin_url: updated.linkedin_url,
    phone: updated.phone,
    last_seen_at: updated.last_seen_at,
  }
}

/**
 * How far an event's own cursor may move the stored position.
 *
 * `sync` is the server's word about a page it scoped itself. `live` is a user
 * topic event, which is trustworthy only while it is contiguous. `hold` is a
 * room topic event: that topic carries one room's slice of the log, so its
 * cursor says nothing about what else has happened.
 */
type CursorAdvance = 'sync' | 'live' | 'hold'

const KEY_ROOMS = 'rooms'
const KEY_CONNECTION = 'connection'
const KEY_PRESENCE = 'presence'
const keyMessages = (roomId: UUID): string => `messages:${roomId}`
const keyTyping = (roomId: UUID): string => `typing:${roomId}`

class SyncStore {
  private readonly listeners = new Map<string, Set<() => void>>()
  private readonly eventListeners = new Set<(event: DurableEvent) => void>()
  private readonly dirty = new Set<string>()
  private batching = 0

  private cursor: Cursor = 0
  private ready = false
  private self: User | null = null

  private readonly rooms = new Map<UUID, Room>()
  private roomsSnapshot: SyncSnapshot | null = null

  private connection: ConnectionSnapshot = { status: 'connecting', lastError: null }

  private readonly messages = new Map<UUID, RoomMessageCache>()
  private readonly pending = new Map<string, PendingMessage>()

  private readonly typing = new Map<UUID, Map<UUID, number>>()
  private readonly typingSnapshots = new Map<UUID, UUID[]>()
  private typingSweep: ReturnType<typeof setInterval> | null = null

  private readonly presence = new Map<UUID, PresenceEntry>()
  private presenceSnapshot: ReadonlyMap<UUID, PresenceEntry> | null = null

  private readonly appliedEvents = new Set<UUID>()
  private readonly appliedOrder: UUID[] = []

  // ── Subscription plumbing ───────────────────────────────────────────────

  subscribe(key: string, listener: () => void): () => void {
    let set = this.listeners.get(key)
    if (set === undefined) {
      set = new Set<() => void>()
      this.listeners.set(key, set)
    }
    set.add(listener)

    return () => {
      const current = this.listeners.get(key)
      if (current === undefined) return
      current.delete(listener)
      if (current.size === 0) this.listeners.delete(key)
    }
  }

  /** Escape hatch for layers that keep their own caches, such as query hooks. */
  subscribeToEvents(listener: (event: DurableEvent) => void): () => void {
    this.eventListeners.add(listener)
    return () => {
      this.eventListeners.delete(listener)
    }
  }

  private touch(key: string): void {
    this.invalidate(key)
    if (this.batching > 0) this.dirty.add(key)
    else this.notify(key)
  }

  private notify(key: string): void {
    const set = this.listeners.get(key)
    if (set === undefined) return
    for (const listener of [...set]) listener()
  }

  private invalidate(key: string): void {
    if (key === KEY_ROOMS) {
      this.roomsSnapshot = null
      return
    }
    if (key === KEY_PRESENCE) {
      this.presenceSnapshot = null
      return
    }
    if (key.startsWith('messages:')) {
      const cache = this.messages.get(key.slice('messages:'.length))
      if (cache !== undefined) cache.snapshot = null
      return
    }
    if (key.startsWith('typing:')) {
      this.typingSnapshots.delete(key.slice('typing:'.length))
    }
  }

  /** Applies a batch of changes and notifies each affected slice exactly once. */
  private batch<T>(work: () => T): T {
    this.batching += 1
    try {
      return work()
    } finally {
      this.batching -= 1
      if (this.batching === 0) {
        const keys = [...this.dirty]
        this.dirty.clear()
        for (const key of keys) this.notify(key)
      }
    }
  }

  // ── Reads ───────────────────────────────────────────────────────────────

  getCursor(): Cursor {
    return this.cursor
  }

  syncSnapshot(): SyncSnapshot {
    this.roomsSnapshot ??= {
      rooms: [...this.rooms.values()],
      cursor: this.cursor,
      ready: this.ready,
    }
    return this.roomsSnapshot
  }

  connectionSnapshot(): ConnectionSnapshot {
    return this.connection
  }

  messagesSnapshot(roomId: UUID): RoomMessagesSnapshot {
    const cache = this.roomCache(roomId)
    if (cache.snapshot !== null) return cache.snapshot

    const pending = [...this.pending.values()]
      .filter((entry) => entry.room_id === roomId)
      .sort((left, right) => left.created_at.localeCompare(right.created_at))

    const self = this.self
    const optimistic =
      self === null
        ? []
        : pending.map((entry, index) => optimisticMessage(entry, self, index))

    cache.snapshot = {
      messages: [...cache.ordered, ...optimistic],
      pending,
      hasMore: cache.hasMore,
      loading: cache.loading,
      error: cache.error,
    }
    return cache.snapshot
  }

  typingSnapshot(roomId: UUID): UUID[] {
    const cached = this.typingSnapshots.get(roomId)
    if (cached !== undefined) return cached

    const now = Date.now()
    const room = this.typing.get(roomId)
    const users =
      room === undefined
        ? []
        : [...room.entries()]
            .filter(([, expiresAt]) => expiresAt > now)
            .map(([userId]) => userId)

    this.typingSnapshots.set(roomId, users)
    return users
  }

  presenceSnapshotOf(): ReadonlyMap<UUID, PresenceEntry> {
    this.presenceSnapshot ??= new Map(this.presence)
    return this.presenceSnapshot
  }

  hasHistory(roomId: UUID): boolean {
    return this.roomCache(roomId).loadedHistory
  }

  hasOlderHistory(roomId: UUID): boolean {
    return this.roomCache(roomId).hasMore
  }

  oldestSequence(roomId: UUID): number | null {
    // A separately fetched thread may be far older than the contiguous room
    // pages. It must not move pagination past messages we have never fetched.
    return this.roomCache(roomId).historyBefore
  }

  getPending(): PendingMessage[] {
    return [...this.pending.values()]
  }

  hasConfirmed(roomId: UUID, clientMessageId: string): boolean {
    return this.roomCache(roomId).byClientId.has(clientMessageId)
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  setSelf(user: User | null): void {
    this.self = user
    this.touch(KEY_ROOMS)
  }

  /**
   * Replaces a person's current profile in every message held by this client.
   *
   * Messages deliberately keep only a sender id, not a profile snapshot, on
   * the server. The local cache does contain the response it last received,
   * though, so profile edits must refresh those already-rendered messages too.
   */
  refreshUser(user: User): Message[] {
    const updatedMessages: Message[] = []

    this.batch(() => {
      const self = this.self
      const isSelf = self !== null && self.id === user.id
      if (isSelf && self !== null) this.self = userWithCurrentProfile(self, user)

      const touchedRooms = new Set<UUID>()
      for (const [roomId, cache] of this.messages) {
        for (const message of cache.ordered) {
          if (message.sender.id !== user.id) continue

          const refreshed: Message = {
            ...message,
            sender: userWithCurrentProfile(message.sender, user),
          }
          this.insert(cache, refreshed)
          updatedMessages.push(refreshed)
          touchedRooms.add(roomId)
        }
      }

      // Pending messages borrow `self` when rendered, so their room also needs
      // a new snapshot even if no confirmed message from this person is held.
      if (isSelf) {
        for (const pending of this.pending.values()) touchedRooms.add(pending.room_id)
      }

      for (const roomId of touchedRooms) this.touch(keyMessages(roomId))
    })

    return updatedMessages
  }

  setReady(ready: boolean): void {
    if (this.ready === ready) return
    this.ready = ready
    this.touch(KEY_ROOMS)
  }

  setConnection(status: ConnectionStatus, lastError: string | null): void {
    if (this.connection.status === status && this.connection.lastError === lastError) return
    this.connection = { status, lastError }
    this.touch(KEY_CONNECTION)
  }

  /** Empties everything but the connection state. Used on reset and sign-out. */
  reset(): void {
    this.batch(() => {
      const roomIds = [...this.messages.keys()]
      this.cursor = 0
      this.ready = false
      this.rooms.clear()
      this.messages.clear()
      this.pending.clear()
      this.typing.clear()
      this.typingSnapshots.clear()
      this.presence.clear()
      this.appliedEvents.clear()
      this.appliedOrder.length = 0
      this.stopTypingSweep()

      this.touch(KEY_ROOMS)
      this.touch(KEY_PRESENCE)
      for (const roomId of roomIds) {
        this.touch(keyMessages(roomId))
        this.touch(keyTyping(roomId))
      }
    })
  }

  // ── Hydration from cache ────────────────────────────────────────────────

  hydrate(rooms: readonly Room[], cursor: Cursor, pending: readonly PendingMessage[]): void {
    this.batch(() => {
      for (const room of rooms) this.rooms.set(room.id, room)
      this.cursor = Math.max(this.cursor, cursor)

      for (const entry of pending) {
        this.pending.set(entry.client_message_id, entry)
        this.touch(keyMessages(entry.room_id))
      }

      this.touch(KEY_ROOMS)
    })
  }

  // ── Applying the log ────────────────────────────────────────────────────

  applySync(response: SyncResponse, bootstrap: boolean): ApplyOutcome {
    return this.batch(() => {
      const outcome = emptyOutcome()

      if (bootstrap) {
        // A bootstrap is authoritative about membership, so rooms the user has
        // left disappear instead of lingering from the cache.
        for (const roomId of [...this.rooms.keys()]) {
          if (response.rooms.some((room) => room.id === roomId)) continue
          this.dropRoom(roomId, outcome)
        }
      }

      for (const room of response.rooms) this.putRoom(room, outcome)
      for (const event of response.events) this.applyDurable(event, 'sync', outcome)
      for (const [roomId, unread] of Object.entries(response.read_states ?? {})) {
        const room = this.rooms.get(roomId)
        if (room !== undefined) this.putRoom({ ...room, unread }, outcome)
      }

      this.advanceCursor(response.cursor)
      this.ready = true
      this.touch(KEY_ROOMS)

      return outcome
    })
  }

  applyLiveEvents(events: readonly DurableEvent[], advance: boolean): ApplyOutcome {
    return this.batch(() => {
      const outcome = emptyOutcome()
      const mode: CursorAdvance = advance ? 'live' : 'hold'
      for (const event of events) this.applyDurable(event, mode, outcome)
      return outcome
    })
  }

  /** A room fetched because the log said this account had been added to it. */
  adoptRoom(room: Room): ApplyOutcome {
    return this.batch(() => {
      const outcome = emptyOutcome()
      this.putRoom(room, outcome)
      return outcome
    })
  }

  /** A complete message returned directly by a mutation such as reacting. */
  adoptMessage(message: Message): ApplyOutcome {
    return this.batch(() => {
      const outcome = emptyOutcome()
      this.putMessage(message, false, outcome)
      return outcome
    })
  }

  applyEphemeral(event: EphemeralEvent): Room | null {
    switch (event.type) {
      case 'typing.started':
        if (event.room_id !== null) this.noteTyping(event.room_id, event.payload.user_id, true)
        return null
      case 'typing.stopped':
        if (event.room_id !== null) this.noteTyping(event.room_id, event.payload.user_id, false)
        return null
      case 'presence.changed':
        this.presence.set(event.payload.user_id, {
          status: event.payload.status,
          last_seen_at: event.payload.last_seen_at,
        })
        this.touch(KEY_PRESENCE)
        return null
      case 'read.updated': {
        if (event.room_id === null) return null
        const room = this.rooms.get(event.room_id)
        if (room === undefined) return null
        const updated = { ...room, unread: event.payload.unread }
        this.rooms.set(event.room_id, updated)
        this.touch(KEY_ROOMS)
        return updated
      }
      default:
        assertNever(event)
    }
  }

  private advanceCursor(cursor: Cursor): void {
    if (cursor <= this.cursor) return
    this.cursor = cursor
    this.touch(KEY_ROOMS)
  }

  /**
   * The cursor after a live event.
   *
   * The socket delivers the rooms this account is a member of, while `/sync` is
   * scoped to every room it may read — this server's public channels included,
   * joined or not. Cursors are gap-free server-wide, so a live event that skips
   * one stands for an event `/sync` would have handed over. Storing the higher
   * number would put that event permanently below the cursor, so the position
   * holds and the gap is reported instead.
   */
  private advanceLive(cursor: Cursor, outcome: ApplyOutcome): void {
    if (cursor <= this.cursor) return
    if (cursor > this.cursor + 1) {
      outcome.gap = true
      return
    }
    this.cursor = cursor
    this.touch(KEY_ROOMS)
  }

  private advance(event: DurableEvent, mode: CursorAdvance, outcome: ApplyOutcome): void {
    if (mode === 'sync') this.advanceCursor(event.cursor)
    else if (mode === 'live') this.advanceLive(event.cursor, outcome)
  }

  private applyDurable(event: DurableEvent, mode: CursorAdvance, outcome: ApplyOutcome): void {
    // The user and room topics both carry an open room's events, so the same
    // event can arrive twice. Remembering ids keeps derived counters — unread,
    // above all — from being applied more than once.
    if (this.appliedEvents.has(event.event_id)) {
      this.advance(event, mode, outcome)
      return
    }
    this.remember(event.event_id)

    switch (event.type) {
      case 'message.created':
      case 'message.updated': {
        const message = event.payload.message
        this.putMessage(message, event.type === 'message.created', outcome)
        break
      }
      case 'message.deleted': {
        this.tombstone(event.room_id, event.payload.message_id, event.payload.deleted_at, outcome)
        break
      }
      case 'message.reaction.added':
      case 'message.reaction.removed': {
        this.applyReaction(event, outcome)
        break
      }
      case 'room.created':
      case 'room.updated': {
        this.mergeRoom(event.payload.room, outcome)
        break
      }
      case 'room.deleted': {
        this.dropRoom(event.payload.room_id, outcome)
        break
      }
      case 'member.joined': {
        const self = this.self
        // The only event emitted when someone is added to a room that already
        // exists. A private channel or an existing group DM has never been in
        // anything this client was sent, so being named here is the first and
        // last word about it: the room has to be fetched or it stays invisible.
        const joinedSelf = self !== null && event.payload.member.user.id === self.id
        const held = this.rooms.get(event.room_id)
        if (joinedSelf && held === undefined) {
          outcome.joinedRooms.push(event.room_id)
        } else if (joinedSelf && held !== undefined) {
          this.putRoom(
            {
              ...held,
              // A self-join mutation may already have applied the endpoint's
              // room response. In that case the count is authoritative and
              // this event only confirms it; an invitation arriving on its
              // own still increments the available-channel copy we held.
              member_count:
                held.my_membership === null ? held.member_count + 1 : held.member_count,
              my_membership: {
                ...held.my_membership,
                id: event.payload.member.id,
                role: event.payload.member.role,
                joined_at: event.payload.member.joined_at,
                muted: event.payload.member.muted,
                starred:
                  event.payload.member.starred ?? held.my_membership?.starred ?? false,
                notification:
                  event.payload.member.notification ??
                  held.my_membership?.notification ??
                  'default',
              },
              unread: held.unread ?? { count: 0, mentions: 0, last_read_sequence: 0 },
            },
            outcome,
          )
        } else {
          this.adjustMemberCount(event.room_id, 1, outcome)
        }
        break
      }
      case 'member.left': {
        const self = this.self
        if (self !== null && event.payload.user_id === self.id) {
          this.dropRoom(event.room_id, outcome)
        } else {
          this.adjustMemberCount(event.room_id, -1, outcome)
        }
        break
      }
      case 'member.updated': {
        const self = this.self
        const room = this.rooms.get(event.room_id)
        if (room !== undefined && self !== null && event.payload.member.user.id === self.id) {
          this.putRoom(
            {
              ...room,
              my_membership: {
                ...room.my_membership,
                id: event.payload.member.id,
                role: event.payload.member.role,
                joined_at: event.payload.member.joined_at,
                muted: event.payload.member.muted,
                // Starred and notification preferences are local to the
                // viewer and intentionally absent from room-wide membership
                // events. Keep the authoritative per-caller values already
                // returned by GET /rooms (or by the optimistic mutation).
                starred:
                  event.payload.member.starred ?? room.my_membership?.starred ?? false,
                notification:
                  event.payload.member.notification ??
                  room.my_membership?.notification ??
                  'default',
              },
            },
            outcome,
          )
        }
        break
      }
      case 'attachment.created':
      case 'attachment.ready': {
        this.putAttachment(event.room_id, event.payload.attachment, outcome)
        break
      }
      case 'attachment.deleted': {
        this.dropAttachment(event.room_id, event.payload.attachment_id, outcome)
        break
      }
      default:
        assertNever(event)
    }

    this.advance(event, mode, outcome)

    for (const listener of [...this.eventListeners]) {
      try {
        listener(event)
      } catch {
        // A consumer's cache invalidation must never stall the log.
      }
    }
  }

  private remember(eventId: UUID): void {
    this.appliedEvents.add(eventId)
    this.appliedOrder.push(eventId)
    while (this.appliedOrder.length > APPLIED_EVENT_MEMORY) {
      const oldest = this.appliedOrder.shift()
      if (oldest !== undefined) this.appliedEvents.delete(oldest)
    }
  }

  // ── Rooms ───────────────────────────────────────────────────────────────

  private putRoom(room: Room, outcome: ApplyOutcome): void {
    this.rooms.set(room.id, room)
    outcome.rooms.push(room)
    this.touch(KEY_ROOMS)
  }

  /**
   * A room as an event describes it, over the copy already held.
   *
   * `my_membership` and `unread` are per-caller and one `room.updated` goes to
   * every member, so the server leaves both out. Replacing the room wholesale
   * would therefore drop the viewer's own membership and unread badge every
   * time a channel was renamed — and nothing would put them back, because an
   * incremental `/sync` answers `rooms: []` by design.
   *
   * A room the store has never held has nothing to merge with, so it is taken
   * as it comes and then fetched: the creator of a channel is a member of it
   * from the first moment, and an event that cannot say so would otherwise
   * leave them reading their own new channel from behind the glass.
   */
  private mergeRoom(room: Room, outcome: ApplyOutcome): void {
    const held = this.rooms.get(room.id)
    if (held === undefined) {
      this.putRoom(room, outcome)
      // Nothing was held, so there is no membership to keep and the event
      // carries none — which reads as "not a member" for a room this account
      // may well have just created. Only `GET /rooms/:id` knows, so ask.
      outcome.joinedRooms.push(room.id)
      return
    }

    this.putRoom(
      {
        ...room,
        my_membership: room.my_membership ?? held.my_membership,
        unread: room.unread ?? held.unread,
      },
      outcome,
    )
  }

  private dropRoom(roomId: UUID, outcome: ApplyOutcome): void {
    if (!this.rooms.delete(roomId) && !this.messages.has(roomId)) return

    this.messages.delete(roomId)
    this.typing.delete(roomId)
    this.typingSnapshots.delete(roomId)
    for (const [key, entry] of this.pending) {
      if (entry.room_id === roomId) this.pending.delete(key)
    }

    outcome.removedRooms.push(roomId)
    this.touch(KEY_ROOMS)
    this.touch(keyMessages(roomId))
    this.touch(keyTyping(roomId))
  }

  private adjustMemberCount(roomId: UUID, delta: number, outcome: ApplyOutcome): void {
    const room = this.rooms.get(roomId)
    if (room === undefined) return
    this.putRoom({ ...room, member_count: Math.max(0, room.member_count + delta) }, outcome)
  }

  /** Optimistic read state. The next bootstrap replaces it with the server's. */
  noteRead(roomId: UUID, sequence: number): Room | null {
    const room = this.rooms.get(roomId)
    if (room === undefined) return null
    const current = unreadOf(room)
    if (current.count === 0 && current.last_read_sequence >= sequence) return null

    const unread: RoomUnread = {
      count: 0,
      mentions: 0,
      last_read_sequence: Math.max(current.last_read_sequence, sequence),
    }
    const updated: Room = { ...room, unread }
    this.rooms.set(roomId, updated)
    this.touch(KEY_ROOMS)
    return updated
  }

  /**
   * The unread counters after a message lands.
   *
   * Derived locally rather than waited for, so a room's badge moves the instant
   * the message does. Applying it twice would be visible, which is why every
   * event is de-duplicated on `event_id` before it reaches here.
   */
  private nextUnread(room: Room, message: Message, fresh: boolean): RoomUnread {
    const self = this.self
    const current = unreadOf(room)
    if (!fresh || self === null) return current
    if (message.sender.id === self.id) return current
    if (message.room_sequence <= current.last_read_sequence) return current

    const mentioned = message.mentions.includes(self.id) || message.mentions_everyone
    // Channel thread replies are hidden from the main list. They must not
    // light the room badge; a mention in that reply still does.
    const channelReply =
      room.kind === 'channel' && message.parent_id != null && message.parent_id !== ''
    return {
      count: current.count + (channelReply ? 0 : 1),
      mentions: current.mentions + (mentioned ? 1 : 0),
      last_read_sequence: current.last_read_sequence,
    }
  }

  // ── Messages ────────────────────────────────────────────────────────────

  private roomCache(roomId: UUID): RoomMessageCache {
    let cache = this.messages.get(roomId)
    if (cache === undefined) {
      cache = emptyRoomCache()
      this.messages.set(roomId, cache)
    }
    return cache
  }

  private insert(cache: RoomMessageCache, message: Message): void {
    const normalised = normalizeMessage(message)
    const { index, found } = locate(cache.ordered, normalised.room_sequence)
    if (found) cache.ordered[index] = normalised
    else cache.ordered.splice(index, 0, normalised)

    cache.byId.set(normalised.id, normalised)
    if (normalised.client_message_id !== null) {
      cache.byClientId.set(normalised.client_message_id, normalised.id)
    }
  }

  private putMessage(message: Message, created: boolean, outcome: ApplyOutcome): void {
    const cache = this.roomCache(message.room_id)
    const existing = cache.byId.get(message.id)
    this.insert(cache, message)
    outcome.messages.push(message)

    // Reconciled on client_message_id, never on id: the server may already have
    // handed us this message over the socket while the POST was still in the
    // air, and the two deliveries share nothing but that field.
    if (message.client_message_id !== null && this.pending.has(message.client_message_id)) {
      this.pending.delete(message.client_message_id)
      outcome.confirmed.push(message.client_message_id)
    }

    const room = this.rooms.get(message.room_id)
    if (room !== undefined) {
      const unread = this.nextUnread(room, message, created && existing === undefined)
      const lastSequence = Math.max(room.last_sequence, message.room_sequence)

      if (unread !== room.unread || lastSequence !== room.last_sequence) {
        const updated: Room = { ...room, unread, last_sequence: lastSequence }
        this.rooms.set(room.id, updated)
        // Queued for persistence too, so a reload does not resurrect a badge
        // the user has already cleared.
        outcome.rooms.push(updated)
        this.touch(KEY_ROOMS)
      }
    }

    this.touch(keyMessages(message.room_id))
  }

  private tombstone(
    roomId: UUID,
    messageId: UUID,
    deletedAt: Timestamp,
    outcome: ApplyOutcome,
  ): void {
    const cache = this.roomCache(roomId)
    const existing = cache.byId.get(messageId)
    // Nothing loaded to tombstone; history will fetch it in its deleted form.
    if (existing === undefined) return

    const tombstoned: Message = {
      ...existing,
      text: '',
      attachments: [],
      reactions: [],
      deleted_at: deletedAt,
    }
    this.insert(cache, tombstoned)
    outcome.messages.push(tombstoned)
    this.touch(keyMessages(roomId))
  }

  private applyReaction(
    event: Extract<DurableEvent, { type: 'message.reaction.added' | 'message.reaction.removed' }>,
    outcome: ApplyOutcome,
  ): void {
    const cache = this.roomCache(event.room_id)
    const existing = cache.byId.get(event.payload.message_id)
    if (existing === undefined) return

    const count = Math.max(0, event.payload.count)
    const actor = event.payload.actor
    const mine = actor?.qualified_id != null && actor.qualified_id === this.self?.qualified_id
    const reactions = [...(existing.reactions ?? [])]
    const index = reactions.findIndex((reaction) => reaction.emoji === event.payload.emoji)

    if (index >= 0) {
      const previous = reactions[index]
      if (previous === undefined) return
      if (count === 0) reactions.splice(index, 1)
      else {
        reactions[index] = {
          emoji: event.payload.emoji,
          count,
          me:
            event.type === 'message.reaction.added'
              ? previous.me || mine
              : mine
                ? false
                : previous.me,
        }
      }
    } else if (count > 0 && event.type === 'message.reaction.added') {
      reactions.push({ emoji: event.payload.emoji, count, me: mine })
    }

    const updated = { ...existing, reactions }
    this.insert(cache, updated)
    outcome.messages.push(updated)
    this.touch(keyMessages(event.room_id))
  }

  private putAttachment(roomId: UUID, attachment: Attachment, outcome: ApplyOutcome): void {
    if (attachment.message_id === null) return

    const cache = this.roomCache(roomId)
    const message = cache.byId.get(attachment.message_id)
    if (message === undefined) return

    const attachments = message.attachments.some((entry) => entry.id === attachment.id)
      ? message.attachments.map((entry) => (entry.id === attachment.id ? attachment : entry))
      : [...message.attachments, attachment]

    const updated: Message = { ...message, attachments }
    this.insert(cache, updated)
    outcome.messages.push(updated)
    this.touch(keyMessages(roomId))
  }

  private dropAttachment(roomId: UUID, attachmentId: UUID, outcome: ApplyOutcome): void {
    const cache = this.roomCache(roomId)

    for (const message of cache.ordered) {
      if (!message.attachments.some((entry) => entry.id === attachmentId)) continue

      const updated: Message = {
        ...message,
        attachments: message.attachments.filter((entry) => entry.id !== attachmentId),
      }
      this.insert(cache, updated)
      outcome.messages.push(updated)
      this.touch(keyMessages(roomId))
      return
    }
  }

  // ── History paging ──────────────────────────────────────────────────────

  applyCachedHistory(roomId: UUID, messages: readonly Message[]): void {
    if (messages.length === 0) return

    this.batch(() => {
      const cache = this.roomCache(roomId)
      for (const message of messages) this.insert(cache, message)
      this.touch(keyMessages(roomId))
    })
  }

  /**
   * The newest page from the server.
   *
   * If it does not touch what is already held, the cached block is stale and
   * disconnected — showing both would render a contiguous conversation that
   * never happened, so the older block is dropped and refetched on scroll.
   */
  applyNewestPage(roomId: UUID, page: readonly Message[], hasMore: boolean): void {
    this.batch(() => {
      const cache = this.roomCache(roomId)
      const ascending = [...page].sort((left, right) => left.room_sequence - right.room_sequence)

      const oldestInPage = ascending[0]
      const newestHeld = cache.ordered[cache.ordered.length - 1]
      if (
        oldestInPage !== undefined &&
        newestHeld !== undefined &&
        oldestInPage.room_sequence > newestHeld.room_sequence + 1
      ) {
        cache.ordered = []
        cache.byId.clear()
        cache.byClientId.clear()
      }

      for (const message of ascending) this.insert(cache, message)
      cache.historyBefore = oldestInPage?.room_sequence ?? null
      cache.hasMore = hasMore
      cache.loadedHistory = true
      cache.error = null
      this.touch(keyMessages(roomId))
    })
  }

  applyOlderPage(roomId: UUID, page: readonly Message[], hasMore: boolean): void {
    this.batch(() => {
      const cache = this.roomCache(roomId)
      for (const message of page) this.insert(cache, message)
      if (page.length > 0) cache.historyBefore = Math.min(...page.map((message) => message.room_sequence))
      cache.hasMore = hasMore
      cache.loadedHistory = true
      cache.error = null
      this.touch(keyMessages(roomId))
    })
  }

  setHistoryLoading(roomId: UUID, loading: boolean): void {
    const cache = this.roomCache(roomId)
    if (cache.loading === loading) return
    cache.loading = loading
    this.touch(keyMessages(roomId))
  }

  setHistoryError(roomId: UUID, error: string | null): void {
    const cache = this.roomCache(roomId)
    if (cache.error === error) return
    cache.error = error
    this.touch(keyMessages(roomId))
  }

  // ── The outbox ──────────────────────────────────────────────────────────

  putPending(entry: PendingMessage): void {
    this.pending.set(entry.client_message_id, entry)
    this.touch(keyMessages(entry.room_id))
  }

  getPendingEntry(clientMessageId: string): PendingMessage | null {
    return this.pending.get(clientMessageId) ?? null
  }

  failPending(clientMessageId: string, error: string, retryable: boolean): PendingMessage | null {
    const entry = this.pending.get(clientMessageId)
    if (entry === undefined) return null

    const failed: PendingMessage = { ...entry, status: 'failed', error, retryable }
    this.pending.set(clientMessageId, failed)
    this.touch(keyMessages(entry.room_id))
    return failed
  }

  confirmPending(clientMessageId: string, message: Message): ApplyOutcome {
    return this.batch(() => {
      const outcome = emptyOutcome()
      this.putMessage(message, true, outcome)

      if (this.pending.delete(clientMessageId)) {
        outcome.confirmed.push(clientMessageId)
        this.touch(keyMessages(message.room_id))
      }

      return outcome
    })
  }

  dropPending(clientMessageId: string): void {
    const entry = this.pending.get(clientMessageId)
    if (entry === undefined) return
    this.pending.delete(clientMessageId)
    this.touch(keyMessages(entry.room_id))
  }

  // ── Typing ──────────────────────────────────────────────────────────────

  private noteTyping(roomId: UUID, userId: UUID, active: boolean): void {
    let room = this.typing.get(roomId)

    if (!active) {
      if (room === undefined || !room.delete(userId)) return
      this.touch(keyTyping(roomId))
      return
    }

    if (room === undefined) {
      room = new Map<UUID, number>()
      this.typing.set(roomId, room)
    }
    room.set(userId, Date.now() + TYPING_TTL_MS)
    this.touch(keyTyping(roomId))
    this.startTypingSweep()
  }

  private startTypingSweep(): void {
    if (this.typingSweep !== null) return
    if (typeof setInterval !== 'function') return

    this.typingSweep = setInterval(() => {
      const now = Date.now()
      let active = 0

      this.batch(() => {
        for (const [roomId, users] of this.typing) {
          let changed = false
          for (const [userId, expiresAt] of users) {
            if (expiresAt > now) continue
            users.delete(userId)
            changed = true
          }
          if (users.size === 0) this.typing.delete(roomId)
          else active += users.size
          if (changed) this.touch(keyTyping(roomId))
        }
      })

      if (active === 0) this.stopTypingSweep()
    }, TYPING_SWEEP_MS)
  }

  private stopTypingSweep(): void {
    if (this.typingSweep === null) return
    clearInterval(this.typingSweep)
    this.typingSweep = null
  }
}

function normalizeMessage(message: Message): Message {
  return {
    ...message,
    parent_id: message.parent_id ?? null,
    reply_count: typeof message.reply_count === 'number' ? message.reply_count : 0,
    blocks: Array.isArray(message.blocks) ? message.blocks : [],
    sender_avatar_url: message.sender_avatar_url ?? null,
    reactions: Array.isArray(message.reactions) ? message.reactions : [],
  }
}

function optimisticMessage(entry: PendingMessage, self: User, offset: number): Message {
  return {
    id: `${OPTIMISTIC_ID_PREFIX}${entry.client_message_id}`,
    room_id: entry.room_id,
    room_sequence: OPTIMISTIC_SEQUENCE_BASE + offset,
    parent_id: entry.parent_id ?? null,
    reply_count: 0,
    text: entry.text,
    sender: self,
    sender_override: null,
    client_message_id: entry.client_message_id,
    // Mentions are resolved server-side; the echo carries the real list.
    mentions: [],
    mentions_everyone: false,
    attachments: entry.attachments,
    reactions: [],
    edited_at: null,
    deleted_at: null,
    created_at: entry.created_at,
  }
}

// ── Write-behind persistence ────────────────────────────────────────────────

/**
 * Coalesces cache writes. A busy room would otherwise cost two IndexedDB
 * transactions per message, which is exactly the sort of background work that
 * makes a chat client feel heavy.
 */
class Persister {
  private readonly messages = new Map<string, Message>()
  private readonly rooms = new Map<UUID, Room>()
  private readonly avatarUpdates = new Map<UUID, string | null>()
  private cursor: Cursor | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private flushing: Promise<void> = Promise.resolve()

  queue(outcome: ApplyOutcome, cursor: Cursor | null): void {
    for (const message of outcome.messages) {
      this.messages.set(`${message.room_id}:${message.room_sequence}`, message)
    }
    for (const room of outcome.rooms) this.rooms.set(room.id, room)
    if (cursor !== null && (this.cursor === null || cursor > this.cursor)) this.cursor = cursor

    for (const roomId of outcome.removedRooms) {
      this.rooms.delete(roomId)
      for (const key of [...this.messages.keys()]) {
        if (key.startsWith(`${roomId}:`)) this.messages.delete(key)
      }
      void removeRoom(roomId)
    }

    this.schedule()
  }

  queueAvatarUpdate(userId: UUID, avatarUrl: string | null): void {
    this.avatarUpdates.set(userId, avatarUrl)
    this.schedule()
  }

  private schedule(): void {
    if (this.timer !== null) return
    this.timer = setTimeout(() => {
      this.timer = null
      void this.flush()
    }, PERSIST_DELAY_MS)
  }

  flush(): Promise<void> {
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }

    const messages = [...this.messages.values()]
    const rooms = [...this.rooms.values()]
    const avatarUpdates = [...this.avatarUpdates]
    const cursor = this.cursor
    this.messages.clear()
    this.rooms.clear()
    this.avatarUpdates.clear()
    this.cursor = null

    if (messages.length === 0 && rooms.length === 0 && avatarUpdates.length === 0 && cursor === null) return this.flushing

    this.flushing = this.flushing
      .then(async () => {
        await saveMessages(messages)
        await saveRooms(rooms)
        // This comes after message writes so a previously queued stale sender
        // cannot overwrite a just-saved profile image in local history.
        for (const [userId, avatarUrl] of avatarUpdates) {
          await refreshMessageAvatar(userId, avatarUrl)
        }
        // Written last: a cursor that runs ahead of the data it stands for
        // would make the next `/sync` skip events this cache never received.
        if (cursor !== null) await saveCursor(cursor)
      })
      // A rejection here would poison every later flush chained onto it, and
      // the cache is already responsible for degrading on its own.
      .catch(() => undefined)

    return this.flushing
  }
}

// ── The engine ──────────────────────────────────────────────────────────────

class SyncEngine {
  private socket: RelaiiSocket | null = null
  private user: User | null = null
  private generation = 0
  private running = false
  private requests = new AbortController()
  private cleanup: Promise<void> = Promise.resolve()
  private readonly writes = new Set<Promise<void>>()

  private persist(work: () => Promise<void>): Promise<void> {
    const pending = work()
    this.writes.add(pending)
    void pending.finally(() => this.writes.delete(pending)).catch(() => undefined)
    return pending
  }

  /** Live events are buffered, not applied, until `/sync` has caught up. */
  private live = false
  private buffer: DurableEvent[] = []

  private syncing: Promise<void> | null = null
  private resyncQueued = false
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private retries = 0

  /** Pending fill-in for a cursor a live event skipped. See `advanceLive`. */
  private catchUpTimer: ReturnType<typeof setTimeout> | null = null

  /** The last flush `stop()` started, so `signOut` can wait for it to land. */
  private lastFlush: Promise<void> = Promise.resolve()

  private readonly adopting = new Set<UUID>()
  private readonly roomRefs = new Map<UUID, number>()
  private readonly historyRequests = new Map<UUID, Promise<void>>()
  private readonly typingSentAt = new Map<UUID, number>()
  private readonly typingTimers = new Map<UUID, ReturnType<typeof setTimeout>>()
  private mounted = 0

  constructor(
    private readonly store: SyncStore,
    private readonly persister: Persister,
  ) {}

  // ── Lifecycle ───────────────────────────────────────────────────────────

  retain(): void {
    this.mounted += 1
  }

  release(): void {
    this.mounted = Math.max(0, this.mounted - 1)
    if (this.mounted === 0) this.stop()
  }

  async start(user: User): Promise<void> {
    if (this.running && this.user?.id === user.id) return

    this.stop()
    this.running = true
    this.user = user
    const generation = (this.generation += 1)
    this.requests = new AbortController()
    await this.cleanup
    await this.lastFlush
    if (generation !== this.generation) return

    this.store.setSelf(user)
    this.store.setConnection('connecting', null)
    this.listen()

    // Step 1: paint from cache before anything touches the network.
    try {
      await this.hydrate(user, generation)
    } catch (error) {
      if (generation !== this.generation) return
      // A cache that cannot even be read is not fatal; bootstrap instead.
      this.store.setConnection('connecting', describeError(error))
    }
    if (generation !== this.generation) return

    // Step 2: catch the log up. Step 3 happens whether or not it succeeded —
    // the socket has its own retry loop, and its join runs step 2 again.
    await this.resync()
    if (generation !== this.generation) return

    this.openSocket(user)
    void this.flushOutbox()
  }

  refreshUser(user: User): void {
    if (this.user?.id === user.id) this.user = user
  }

  stop(): void {
    if (!this.running) return

    this.running = false
    // Every in-flight `/sync`, room fetch and delivery checks this against the
    // generation it started in, so nothing that is already in the air writes to
    // a cache the caller is about to clear.
    this.generation += 1
    this.requests.abort()
    this.live = false
    this.buffer = []
    this.syncing = null
    this.resyncQueued = false
    this.clearRetry()
    this.clearCatchUp()
    this.unlisten()

    for (const timer of this.typingTimers.values()) clearTimeout(timer)
    this.typingTimers.clear()
    this.typingSentAt.clear()
    this.historyRequests.clear()
    this.adopting.clear()

    this.socket?.disconnect()
    this.socket = null
    this.lastFlush = Promise.all([this.persister.flush(), ...this.writes]).then(() => undefined)
  }

  /** Sign-out: the cache belongs to a session that no longer exists. */
  async signOut(): Promise<void> {
    if (!this.running && this.user === null) return

    this.stop()
    this.user = null
    this.store.reset()
    this.store.setSelf(null)
    this.store.setConnection('offline', null)

    // IndexedDB runs transactions in the order they were created, so a flush
    // still in the air would land *after* the clear and leave the departing
    // user's rooms, messages and cursor behind — with the identity keys gone,
    // which is what the next account's "different user" guard reads.
    this.cleanup = this.lastFlush.then(() => resetCache())
    await this.cleanup
  }

  private listen(): void {
    if (typeof window === 'undefined') return
    window.addEventListener(SESSION_EXPIRED_EVENT, this.onSessionExpired)
    window.addEventListener('online', this.onOnline)
    window.addEventListener('offline', this.onOffline)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  private unlisten(): void {
    if (typeof window === 'undefined') return
    window.removeEventListener(SESSION_EXPIRED_EVENT, this.onSessionExpired)
    window.removeEventListener('online', this.onOnline)
    window.removeEventListener('offline', this.onOffline)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }

  private readonly onSessionExpired = (): void => {
    // The refresh token is gone; nothing this socket does can succeed, and a
    // reconnect loop against a dead session is just noise.
    this.socket?.disconnect()
    this.socket = null
    this.live = false
    this.store.setConnection('offline', 'Your session has expired. Sign in again to continue.')
  }

  private readonly onOnline = (): void => {
    if (!this.running) return
    this.store.setConnection('reconnecting', null)
    void this.resync()
  }

  private readonly onOffline = (): void => {
    this.live = false
    this.store.setConnection('offline', null)
  }

  private readonly onVisibilityChange = (): void => {
    if (typeof document === 'undefined') return

    if (document.visibilityState === 'hidden') {
      void this.persister.flush()
      return
    }
    // A backgrounded tab has its timers throttled and its socket may have been
    // closed without notice, so treat every return to the foreground as a
    // reconnect: catch up before trusting anything live.
    if (this.running) void this.resync()
  }

  // ── Step 1: hydrate ─────────────────────────────────────────────────────

  private async hydrate(user: User, generation: number): Promise<void> {
    const identity = await loadIdentity()
    if (generation !== this.generation) return

    // The cache belongs to whoever the identity names, and to nobody else: a
    // different account on the same browser must never see the last one's
    // rooms, not even for the frame before `/sync` answers. An *absent*
    // identity is treated the same way rather than trusted, because a cache
    // that lost its identity keys but kept its rows — an interrupted sign-out,
    // a half-applied clear — is exactly the case a "different id" test misses.
    // Clearing an already-empty cache costs one transaction on first sign-in.
    if (identity.user_id !== user.id) {
      await this.persist(() => resetCache())
      if (generation !== this.generation) return
      this.store.reset()
    }
    await this.persist(() => saveIdentity({ user_id: user.id, server_id: user.server_id }))
    if (generation !== this.generation) return

    const [cursor, rooms, outbox] = await Promise.all([loadCursor(), loadRooms(), loadOutbox()])
    if (generation !== this.generation) return
    this.store.hydrate(rooms, cursor, outbox)
    if (rooms.length > 0) this.store.setReady(true)
  }

  // ── Step 2: catch up ────────────────────────────────────────────────────

  resync(): Promise<void> {
    if (this.syncing !== null) {
      this.resyncQueued = true
      return this.syncing
    }

    // Everything the socket delivers from here is held until the drain has
    // finished, so the cursor cannot jump over events `/sync` still owes us.
    this.live = false
    this.clearRetry()
    this.clearCatchUp()

    const generation = this.generation

    const run = (async (): Promise<void> => {
      try {
        do {
          this.resyncQueued = false
          await this.drain(generation)
        } while (this.resyncQueued && this.running)

        if (generation !== this.generation) return
        this.retries = 0
        this.goLive()
      } catch (error) {
        if (generation !== this.generation) return
        this.live = false
        this.store.setConnection('reconnecting', describeError(error))
        this.scheduleRetry()
      } finally {
        if (generation === this.generation) this.syncing = null
      }
    })()

    this.syncing = run
    return run
  }

  private async drain(generation: number): Promise<void> {
    let resets = 0

    for (;;) {
      const since = this.store.getCursor()
      const response = await api.get<SyncResponse>('/sync', {
        since,
        limit: SYNC_PAGE_LIMIT,
      }, this.requests.signal)

      // Signing out while a page was in the air: the store has been emptied and
      // the cache is about to be, so applying this page would write the
      // departing session's rows straight back into both.
      if (generation !== this.generation) return

      if (response.reset) {
        // The stored cursor predates the server's retained history. There is
        // nothing to reconcile: throw the cache away and bootstrap.
        if (resets > 0) throw new Error('The server asked for a reset twice in a row.')
        resets += 1
        await this.persist(() => resetCache())
        if (generation !== this.generation) return
        this.store.reset()
        continue
      }

      const bootstrap = since === 0
      const outcome = this.store.applySync(response, bootstrap)

      await this.persist(async () => {
        if (bootstrap) await replaceRooms(response.rooms)
        else await saveRooms(response.rooms)
        await saveMessages(outcome.messages)
        for (const roomId of outcome.removedRooms) await removeRoom(roomId)
        await saveCursor(response.cursor)
        await this.clearConfirmed(outcome.confirmed)
      })
      if (generation !== this.generation) return
      this.absorb(outcome)

      if (!response.has_more) return
    }
  }

  /**
   * The parts of an outcome that need a request of their own: a room the log
   * says this account has joined but has never described, and a cursor a live
   * event could not be allowed to step over.
   */
  private absorb(outcome: ApplyOutcome): void {
    if (outcome.joinedRooms.length > 0) void this.adoptRooms(outcome.joinedRooms)
    if (outcome.gap) this.scheduleCatchUp()
  }

  /**
   * Fetches a room this client was added to and has never seen.
   *
   * `member.joined` names the member and the room id and nothing else, and a
   * private channel or an existing group DM is in no payload this client has
   * been sent — so without the fetch the room is invisible until a bootstrap,
   * which a client resuming from a stored cursor never runs.
   */
  private async adoptRooms(roomIds: readonly UUID[]): Promise<void> {
    for (const roomId of roomIds) {
      if (!this.running || this.adopting.has(roomId)) continue

      this.adopting.add(roomId)
      const generation = this.generation

      try {
        const room = await api.get<Room>(`/rooms/${roomId}`, undefined, this.requests.signal)
        if (generation !== this.generation) return

        const outcome = this.store.adoptRoom(room)
        await this.persist(() => saveRooms(outcome.rooms))
      } catch {
        // The room list is carried in full by the next bootstrap, so a failure
        // here costs visibility until then rather than correctness.
      } finally {
        if (generation === this.generation) this.adopting.delete(roomId)
      }
    }
  }

  private goLive(): void {
    const buffered = this.buffer
    this.buffer = []

    if (buffered.length > 0) {
      // Anything at or below the cursor arrived through `/sync` already.
      const cursor = this.store.getCursor()
      const fresh = buffered.filter((event) => event.cursor > cursor)
      const outcome = this.store.applyLiveEvents(fresh, true)
      this.persister.queue(outcome, this.store.getCursor())
      void this.clearConfirmed(outcome.confirmed)
      this.absorb(outcome)
    }

    this.live = true
    if (this.socket !== null && this.socket.connectionStatus === 'online') {
      this.store.setConnection('online', null)
    }
  }

  private scheduleRetry(): void {
    if (!this.running || this.retryTimer !== null) return

    this.retries += 1
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      if (this.running) void this.resync()
    }, retryDelay(this.retries))
  }

  private clearRetry(): void {
    if (this.retryTimer === null) return
    clearTimeout(this.retryTimer)
    this.retryTimer = null
  }

  /**
   * Asks `/sync` for the events a live cursor was not allowed to step over.
   *
   * Delayed and coalesced: the gap is filled once for however many events went
   * past in the meantime, rather than once per event.
   */
  private scheduleCatchUp(): void {
    if (!this.running || this.catchUpTimer !== null) return

    this.catchUpTimer = setTimeout(() => {
      this.catchUpTimer = null
      if (this.running) void this.resync()
    }, CATCH_UP_DELAY_MS)
  }

  private clearCatchUp(): void {
    if (this.catchUpTimer === null) return
    clearTimeout(this.catchUpTimer)
    this.catchUpTimer = null
  }

  // ── Step 3-5: the socket ────────────────────────────────────────────────

  private openSocket(user: User): void {
    const generation = this.generation
    const socket = new RelaiiSocket(
      {
        userId: user.id,
        cursor: () => this.store.getCursor(),
      },
      {
        onDurable: (event, origin) => {
          if (generation === this.generation) this.onDurable(event, origin)
        },
        onEphemeral: (event) => {
          if (generation !== this.generation) return
          const room = this.store.applyEphemeral(event)
          if (room !== null) void this.persist(() => saveRooms([room]))
        },
        onStatus: (status, error) => {
          if (generation === this.generation) this.onStatus(status, error)
        },
        onUserJoin: (reply, rejoin) => {
          if (generation === this.generation) this.onUserJoin(reply.missed, rejoin)
        },
      },
    )

    this.socket = socket
    for (const roomId of this.roomRefs.keys()) socket.openRoom(roomId)
    socket.connect()
  }

  private onDurable(event: DurableEvent, origin: EventOrigin): void {
    if (!this.live) {
      if (this.buffer.length >= LIVE_BUFFER_LIMIT) {
        // Too far behind to reconcile by hand; `/sync` is cheaper and safer.
        this.buffer = []
        void this.resync()
        return
      }
      this.buffer.push(event)
      return
    }

    // Only the user topic is a complete, ordered stream. A room topic carries a
    // subset, so honouring its cursor could advance past an event for a room
    // that is not open — and the cursor guarantee would then be a lie.
    const advance = origin === 'user'
    const outcome = this.store.applyLiveEvents([event], advance)
    this.persister.queue(outcome, advance ? this.store.getCursor() : null)
    void this.clearConfirmed(outcome.confirmed)
    this.absorb(outcome)
  }

  private onStatus(status: ConnectionStatus, error: string | null): void {
    if (this.live && status !== 'online') recordDiagnostic('socket_disconnected')
    if (status === 'online' && !this.live) {
      // Joined, but the log has not been caught up yet: still catching up.
      this.store.setConnection('connecting', error)
      return
    }
    if (status !== 'online') this.live = false
    this.store.setConnection(status, error)
  }

  private onUserJoin(missed: number, rejoin: boolean): void {
    // A rejoin means the socket was down for some interval, and `missed` means
    // the server knows it could not replay everything. Either way the log has
    // to be re-read before live events can be trusted again.
    if (rejoin || missed > 0) {
      void this.resync()
      return
    }
    this.goLive()
  }

  // ── Rooms ───────────────────────────────────────────────────────────────

  /** Joins the room topic while at least one view of the room is mounted. */
  openRoom(roomId: UUID): () => void {
    const next = (this.roomRefs.get(roomId) ?? 0) + 1
    this.roomRefs.set(roomId, next)
    if (next === 1) this.socket?.openRoom(roomId)

    let released = false
    return () => {
      if (released) return
      released = true

      const count = (this.roomRefs.get(roomId) ?? 1) - 1
      if (count > 0) {
        this.roomRefs.set(roomId, count)
        return
      }
      this.roomRefs.delete(roomId)
      this.socket?.closeRoom(roomId)
    }
  }

  ensureHistory(roomId: UUID): Promise<void> {
    const generation = this.generation
    const inFlight = this.historyRequests.get(roomId)
    if (inFlight !== undefined) return inFlight
    if (this.store.hasHistory(roomId)) return Promise.resolve()

    return this.track(roomId, async () => {
      const cached = await loadMessages(roomId, { limit: HISTORY_PAGE_LIMIT })
      if (generation !== this.generation) return
      this.store.applyCachedHistory(roomId, cached)

      const page = await api.get<MessagePage>(`/rooms/${roomId}/messages`, {
        limit: HISTORY_PAGE_LIMIT,
      }, this.requests.signal)
      if (generation !== this.generation) return
      this.store.applyNewestPage(roomId, page.messages, page.has_more)
      await this.persist(() => saveMessages(page.messages))
    })
  }

  loadOlder(roomId: UUID): Promise<void> {
    const generation = this.generation
    const inFlight = this.historyRequests.get(roomId)
    if (inFlight !== undefined) return inFlight

    const before = this.store.oldestSequence(roomId)
    if (before === null) return this.ensureHistory(roomId)
    if (!this.store.hasOlderHistory(roomId)) return Promise.resolve()

    return this.track(roomId, async () => {
      const page = await api.get<MessagePage>(`/rooms/${roomId}/messages`, {
        before,
        limit: HISTORY_PAGE_LIMIT,
      }, this.requests.signal)
      if (generation !== this.generation) return
      if (page.has_more && !page.messages.some((message) => message.room_sequence < before)) {
        throw new Error('The history page did not advance. Try again.')
      }
      this.store.applyOlderPage(roomId, page.messages, page.has_more)
      await this.persist(() => saveMessages(page.messages))
    })
  }

  private track(roomId: UUID, work: () => Promise<void>): Promise<void> {
    const generation = this.generation
    this.store.setHistoryLoading(roomId, true)
    this.store.setHistoryError(roomId, null)

    const request = work()
      .catch((error: unknown) => {
        if (generation !== this.generation) return
        this.store.setHistoryError(roomId, describeError(error))
      })
      .finally(() => {
        if (generation !== this.generation) return
        this.historyRequests.delete(roomId)
        this.store.setHistoryLoading(roomId, false)
      })

    this.historyRequests.set(roomId, request)
    return request
  }

  async loadThread(roomId: UUID, rootId: UUID, after = 0): Promise<MessagePage> {
    const generation = this.generation
    const page = await api.get<MessagePage>(`/rooms/${roomId}/messages`, {
      thread: rootId,
      limit: 100,
      after,
    }, this.requests.signal)
    if (generation !== this.generation) throw new DOMException('Session changed.', 'AbortError')
    this.store.applyCachedHistory(roomId, page.messages)
    await this.persist(() => saveMessages(page.messages))
    return page
  }

  // ── Sending ─────────────────────────────────────────────────────────────

  async send(
    roomId: UUID,
    text: string,
    attachmentIds: readonly UUID[],
    attachments: readonly Attachment[],
    parentId?: string | null,
  ): Promise<void> {
    const generation = this.generation
    const entry: PendingMessage = {
      client_message_id: newClientMessageId(),
      room_id: roomId,
      text,
      attachment_ids: [...attachmentIds],
      attachments: [...attachments],
      parent_id: parentId ?? null,
      created_at: new Date().toISOString(),
      status: 'sending',
      error: null,
      attempts: 0,
      retryable: true,
    }

    this.store.putPending(entry)
    await this.persist(() => savePending(entry))
    if (generation !== this.generation) return
    await this.deliver(entry)
  }

  retry(clientMessageId: string): Promise<void> {
    const entry = this.store.getPendingEntry(clientMessageId)
    if (entry === null) return Promise.resolve()
    return this.deliver(entry)
  }

  async discard(clientMessageId: string): Promise<void> {
    this.store.dropPending(clientMessageId)
    await removePending(clientMessageId)
  }

  private async deliver(entry: PendingMessage): Promise<void> {
    const generation = this.generation
    const attempt: PendingMessage = {
      ...entry,
      status: 'sending',
      error: null,
      attempts: entry.attempts + 1,
    }
    this.store.putPending(attempt)
    await this.persist(() => savePending(attempt))
    if (generation !== this.generation) return

    const body: CreateMessageRequest = {
      text: attempt.text,
      client_message_id: attempt.client_message_id,
      attachment_ids: attempt.attachment_ids,
      parent_id: attempt.parent_id ?? undefined,
    }

    try {
      const message = await api.post<Message>(`/rooms/${attempt.room_id}/messages`, body, this.requests.signal)
      if (generation !== this.generation) return
      const outcome = this.store.confirmPending(attempt.client_message_id, message)
      await this.persist(async () => {
        await removePending(attempt.client_message_id)
        await saveMessages(outcome.messages)
        await saveRooms(outcome.rooms)
      })
    } catch (error) {
      if (generation !== this.generation) return
      const failed = this.store.failPending(
        attempt.client_message_id,
        describeError(error),
        !isRefusal(error),
      )
      if (failed !== null) await this.persist(() => savePending(failed))
    }
  }

  /**
   * Retries whatever survived a reload.
   *
   * Safe precisely because the server is idempotent on `client_message_id`: a
   * message that did land comes back as the original rather than as a
   * duplicate, which is the whole reason the field exists.
   *
   * A message the server has already refused outright is left alone. It stays
   * on screen with its error, where the user can retry it by hand or discard
   * it, rather than being posted again on every page load for the life of the
   * cache — the answer would be the same refusal every time.
   */
  private async flushOutbox(): Promise<void> {
    const generation = this.generation
    for (const entry of this.store.getPending()) {
      if (!this.running || generation !== this.generation) return

      if (this.store.hasConfirmed(entry.room_id, entry.client_message_id)) {
        this.store.dropPending(entry.client_message_id)
        await this.persist(() => removePending(entry.client_message_id))
        continue
      }

      // Absent on rows written before the field existed, which are retryable.
      if (entry.retryable === false) continue

      await this.deliver(entry)
    }
  }

  private async clearConfirmed(clientMessageIds: readonly string[]): Promise<void> {
    await this.persist(async () => {
      for (const id of clientMessageIds) await removePending(id)
    })
  }

  // ── Ephemeral out ───────────────────────────────────────────────────────

  setTyping(roomId: UUID, typing: boolean): void {
    const timer = this.typingTimers.get(roomId)
    if (timer !== undefined) {
      clearTimeout(timer)
      this.typingTimers.delete(roomId)
    }

    if (!typing) {
      this.typingSentAt.delete(roomId)
      this.socket?.sendTyping(roomId, false)
      return
    }

    // Throttled: the composer calls this on every keystroke, and the signal is
    // a hint rather than data.
    const now = Date.now()
    if (now - (this.typingSentAt.get(roomId) ?? 0) > TYPING_REPEAT_MS) {
      this.typingSentAt.set(roomId, now)
      this.socket?.sendTyping(roomId, true)
    }

    this.typingTimers.set(
      roomId,
      setTimeout(() => {
        this.typingTimers.delete(roomId)
        this.typingSentAt.delete(roomId)
        this.socket?.sendTyping(roomId, false)
      }, TYPING_IDLE_MS),
    )
  }

  async markRead(roomId: UUID, sequence: number): Promise<void> {
    const generation = this.generation
    const room = this.store.noteRead(roomId, sequence)
    if (room === null) return

    await this.persist(() => saveRooms([room]))
    if (generation !== this.generation) return
    this.socket?.sendRead(roomId, sequence)

    const body: ReadStateRequest = { sequence }
    try {
      await api.post<unknown>(`/rooms/${roomId}/read`, body)
    } catch {
      // Best effort from here: the next bootstrap carries the server's own
      // read state, which is authoritative anyway.
    }
  }
}

// ── Singletons ──────────────────────────────────────────────────────────────

const store = new SyncStore()
const persister = new Persister()
const engine = new SyncEngine(store, persister)

/**
 * Applies a just-saved profile to messages already loaded in this tab.
 *
 * The persister is used rather than writing IndexedDB directly: an older
 * message write may already be queued, and coalescing both writes prevents it
 * from putting a stale avatar back into the cache afterwards.
 */
export function refreshCachedUser(user: User): void {
  engine.refreshUser(user)
  const updatedMessages = store.refreshUser(user)
  if (updatedMessages.length > 0) {
    const outcome = emptyOutcome()
    outcome.messages = updatedMessages
    persister.queue(outcome, null)
  }
  persister.queueAvatarUpdate(user.id, user.avatar_url)
}

/** Applies a room returned by a direct mutation, before its durable event arrives. */
export function refreshCachedRoom(room: Room): void {
  const outcome = store.adoptRoom(room)
  persister.queue(outcome, null)
}

/** Finishes queued IndexedDB writes before a caller exposes a durable UI state. */
export function flushCachedChanges(): Promise<void> {
  return persister.flush()
}

/** Applies the whole message returned by reactions or edits immediately. */
export function refreshCachedMessage(message: Message): void {
  const outcome = store.adoptMessage(message)
  persister.queue(outcome, null)
}

// ── Provider ────────────────────────────────────────────────────────────────

export interface SyncProviderProps {
  children: ReactNode
}

/**
 * Drives the engine from the authenticated session. Renders children
 * immediately: cached state is on screen before the first request goes out.
 */
export function SyncProvider({ children }: SyncProviderProps): ReactElement {
  const { user, server } = useAuth()
  const userId = user?.id ?? null
  const blocked = needsMfaEnrollment(user, server)
  // Held in a ref so the engine reacts to the identity changing rather than to
  // `useAuth` handing back a fresh object on every render.
  const latest = useRef<User | null>(user)
  latest.current = user

  useEffect(() => {
    engine.retain()
    return () => {
      engine.release()
    }
  }, [])

  useEffect(() => {
    const current = latest.current
    if (current === null) void engine.signOut()
    else if (blocked) engine.stop()
    else void engine.start(current)
  }, [blocked, userId])

  return <>{children}</>
}

// ── Hooks ───────────────────────────────────────────────────────────────────

export function useSyncStore(): SyncSnapshot {
  return useSyncExternalStore(
    useCallback((listener: () => void) => store.subscribe(KEY_ROOMS, listener), []),
    useCallback(() => store.syncSnapshot(), []),
  )
}

export function useConnectionState(): ConnectionSnapshot {
  return useSyncExternalStore(
    useCallback((listener: () => void) => store.subscribe(KEY_CONNECTION, listener), []),
    useCallback(() => store.connectionSnapshot(), []),
  )
}

export function useRoomMessages(roomId: string): RoomMessages {
  const snapshot = useSyncExternalStore(
    useCallback(
      (listener: () => void) => store.subscribe(keyMessages(roomId), listener),
      [roomId],
    ),
    useCallback(() => store.messagesSnapshot(roomId), [roomId]),
  )

  // The room topic is joined for exactly as long as a view of it is mounted.
  useEffect(() => engine.openRoom(roomId), [roomId])
  useEffect(() => {
    void engine.ensureHistory(roomId)
  }, [roomId])

  const loadOlder = useCallback(() => {
    void engine.loadOlder(roomId)
  }, [roomId])

  const retry = useCallback((clientMessageId: string) => {
    void engine.retry(clientMessageId)
  }, [])

  const discard = useCallback((clientMessageId: string) => {
    void engine.discard(clientMessageId)
  }, [])

  return useMemo(
    () => ({ ...snapshot, loadOlder, retry, discard }),
    [snapshot, loadOlder, retry, discard],
  )
}

/** User ids currently typing in a room, excluding signals that have gone stale. */
export function useTypingUsers(roomId: string): UUID[] {
  return useSyncExternalStore(
    useCallback((listener: () => void) => store.subscribe(keyTyping(roomId), listener), [roomId]),
    useCallback(() => store.typingSnapshot(roomId), [roomId]),
  )
}

export function usePresence(): ReadonlyMap<UUID, PresenceEntry> {
  return useSyncExternalStore(
    useCallback((listener: () => void) => store.subscribe(KEY_PRESENCE, listener), []),
    useCallback(() => store.presenceSnapshotOf(), []),
  )
}

// ── Imperative surface ──────────────────────────────────────────────────────

/**
 * Sends a message optimistically.
 *
 * Resolves once the attempt has finished either way: a failure leaves the
 * message on screen in a failed state with a retry affordance rather than
 * rejecting, because the user's text must never simply vanish.
 */
export async function sendMessage(
  roomId: string,
  text: string,
  attachmentIds: string[] = [],
  attachments: Attachment[] = [],
  parentId?: string | null,
): Promise<void> {
  if (text.trim() === '' && attachmentIds.length === 0) return
  await engine.send(roomId, text, attachmentIds, attachments, parentId)
}

/** Merges a thread's root and replies into the room cache. */
export function loadThreadMessages(roomId: string, rootId: string, after = 0): Promise<MessagePage> {
  return engine.loadThread(roomId, rootId, after)
}

export function retryMessage(clientMessageId: string): Promise<void> {
  return engine.retry(clientMessageId)
}

export function discardMessage(clientMessageId: string): Promise<void> {
  return engine.discard(clientMessageId)
}

/** Best-effort typing signal, throttled internally. Safe to call per keystroke. */
export function setTyping(roomId: string, typing: boolean): void {
  engine.setTyping(roomId, typing)
}

export function markRoomRead(roomId: string, sequence: number): Promise<void> {
  return engine.markRead(roomId, sequence)
}

/** Forces a catch-up. Live events are held until it completes. */
export function resync(): Promise<void> {
  return engine.resync()
}

/**
 * Observes durable events as they are applied, for layers that keep their own
 * caches — member lists and attachment queries, above all.
 */
export function subscribeToEvents(listener: (event: DurableEvent) => void): () => void {
  return store.subscribeToEvents(listener)
}
